import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import PushToggle from "@/components/PushToggle";
import { Bell, CalendarDays, FileText, Megaphone, ArrowRight } from "lucide-react";

export default function AssocDashboard() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState({ campaigns: [], events: [], notifications: [], documents: [] });

  useEffect(() => {
    Promise.all([
      api.get("/campaigns?active_only=true"),
      api.get("/events"),
      api.get("/notifications"),
      api.get("/documents"),
    ]).then(([c, e, n, d]) => setData({ campaigns: c.data, events: e.data.slice(0, 4), notifications: n.data.slice(0, 4), documents: d.data.slice(0, 4) }));
  }, []);

  return (
    <div className="space-y-8" data-testid="assoc-dashboard">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-accent">{t("nav.zone_assoc")}</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-heading font-bold tracking-tight">{t("dash.greeting", { name: user?.name })}</h1>
        </div>
        <PushToggle />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="rounded-2xl border-2">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="font-heading font-semibold flex items-center gap-2"><Megaphone className="w-4 h-4 text-accent" aria-hidden="true" /> {t("dash.active_campaigns")}</div>
              <Link to="/associat/agenda" className="text-xs text-primary hover:underline">{t("dash.view_all")}</Link>
            </div>
            <div className="space-y-3">
              {data.campaigns.slice(0, 3).map(c => (
                <div key={c.id} className="p-3 rounded-xl bg-secondary/60">
                  <div className="font-medium text-sm">{c.title}</div>
                  <div className="text-xs text-muted-foreground line-clamp-1">{c.description}</div>
                </div>
              ))}
              {data.campaigns.length === 0 && <div className="text-sm text-muted-foreground">{t("dash.no_campaigns")}</div>}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-2">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="font-heading font-semibold flex items-center gap-2"><CalendarDays className="w-4 h-4 text-accent" aria-hidden="true" /> {t("dash.upcoming")}</div>
              <Link to="/associat/agenda" className="text-xs text-primary hover:underline">{t("dash.agenda_link")}</Link>
            </div>
            <div className="space-y-3">
              {data.events.map(e => (
                <div key={e.id} className="p-3 rounded-xl bg-secondary/60 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary text-primary-foreground flex flex-col items-center justify-center text-[10px] font-bold leading-tight">
                    <div>{new Date(e.start_date).toLocaleDateString(i18n.language, { month: "short" }).toUpperCase()}</div>
                    <div className="text-sm">{new Date(e.start_date).getDate()}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{e.title}</div>
                    <div className="text-xs text-muted-foreground truncate">{e.location}</div>
                  </div>
                </div>
              ))}
              {data.events.length === 0 && <div className="text-sm text-muted-foreground">{t("dash.no_events")}</div>}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-2">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="font-heading font-semibold flex items-center gap-2"><Bell className="w-4 h-4 text-accent" aria-hidden="true" /> {t("dash.alerts")}</div>
            </div>
            <div className="space-y-2">
              {data.notifications.map(n => (
                <div key={n.id} className="text-sm">
                  <div className="font-medium">{n.title}</div>
                  <div className="text-xs text-muted-foreground line-clamp-1">{n.body}</div>
                </div>
              ))}
              {data.notifications.length === 0 && <div className="text-sm text-muted-foreground">{t("dash.no_alerts")}</div>}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-2">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="font-heading font-semibold flex items-center gap-2"><FileText className="w-4 h-4 text-accent" aria-hidden="true" /> {t("dash.recent_docs")}</div>
              <Link to="/associat/serveis" className="text-xs text-primary hover:underline">{t("dash.view_all_docs")}</Link>
            </div>
            <div className="space-y-2">
              {data.documents.map(d => (
                <a key={d.id} href={d.file_url} target="_blank" rel="noreferrer" className="flex items-center justify-between p-2 rounded-lg hover:bg-secondary text-sm">
                  <span className="truncate">{d.name}</span>
                  <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                </a>
              ))}
              {data.documents.length === 0 && <div className="text-sm text-muted-foreground">{t("dash.no_docs")}</div>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
